import {Suspense, useEffect, useState} from "react";
/// Components
import {connect, useDispatch} from "react-redux";
import {useNavigate} from "react-router-dom";
// action
import {checkAutoLogin} from "./services/AuthService";
import {isAuthenticated, isLoading} from "./store/selectors/AuthSelectors";
import Router from "./Router";
/// Style
import "./vendor/bootstrap-select/dist/css/bootstrap-select.min.css";
import "./css/style.css";
import "./css/required-fields.css";
import {loadStripe} from "@stripe/stripe-js";
import {useQueryClient} from "@tanstack/react-query";
import {socket} from "./socket/socket";
import {updateProfile} from "./store/actions/AuthActions";
import {requestCurrentPosition} from "./utils/geolocation";
import RequiredFieldIndicators from "./jsx/components/shared/RequiredFieldIndicators";
import AppTranslationBridge from "./i18n/AppTranslationBridge";
import AccessibilityGuard from "./jsx/components/shared/AccessibilityGuard";
import {getStoredAuthToken} from "./utils/authToken";
// import {GoogleOAuthProvider} from "@react-oauth/google";

const stripePublishableKey =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY?.trim() || "";

export const stripeIsConfigured = /^pk_(test|live)_/.test(stripePublishableKey);
export const stripePromise = stripeIsConfigured
  ? loadStripe(stripePublishableKey)
  : null;
function App(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    checkAutoLogin(dispatch, navigate, queryClient);
  }, [dispatch, navigate, queryClient]);
  useEffect(() => {
    // Ask for location as soon as the app opens. Weather and nearby search
    // reuse this same in-memory request, so the browser displays one prompt.
    requestCurrentPosition().catch(() => {
      // Permission errors are handled where location-dependent features render.
    });
  }, []);
  useEffect(() => {
    const handleOnline = async () => {
      setIsOnline(true);
      await queryClient.resumePausedMutations();
      queryClient.refetchQueries({type: "active"});
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [queryClient]);
  useEffect(() => {
    const token = getStoredAuthToken();
    if (!token || !props.isAuthenticated) return undefined;

    const handleEmailVerified = () => {
      if (props.user) {
        dispatch(updateProfile({...props.user, verifiedEmail: true}));
      }
      queryClient.invalidateQueries({queryKey: ["getMe"]});
    };

    const handlePresenceUpdated = ({userId}) => {
      queryClient.invalidateQueries({queryKey: ["Technician"]});
      queryClient.invalidateQueries({queryKey: ["Client"]});
      if (Number(props.user?.id) === Number(userId)) {
        queryClient.invalidateQueries({queryKey: ["getMe"]});
      }
    };
    const handleInterventionUpdated = () => {
      queryClient.invalidateQueries({queryKey: ["interventions"]});
      queryClient.invalidateQueries({queryKey: ["chatRooms"]});
      queryClient.invalidateQueries({queryKey: ["invoices"]});
    };


    socket.on("email_verified", handleEmailVerified);
    socket.on("user_presence_updated", handlePresenceUpdated);

    socket.on("intervention_updated", handleInterventionUpdated);
    // Register listeners before connecting: the server emits the initial
    // presence immediately after authenticating the socket.
    socket.auth = {authorization: "Bearer " + token};
    if (!socket.connected) socket.connect();

    return () => {
      socket.off("email_verified", handleEmailVerified);
      socket.off("user_presence_updated", handlePresenceUpdated);
      socket.off("intervention_updated", handleInterventionUpdated);
    };
  }, [dispatch, props.isAuthenticated, props.user, queryClient]);
  return (
    <>
      <AppTranslationBridge />
      <AccessibilityGuard />
      <Suspense
        fallback={
          <div id="preloader">
            <div className="sk-three-bounce">
              <div className="sk-child sk-bounce1"></div>
              <div className="sk-child sk-bounce2"></div>
              <div className="sk-child sk-bounce3"></div>
            </div>
          </div>
        }
      >
        <RequiredFieldIndicators />
        {!isOnline && (
          <div
            className="fixed bottom-5 right-5 z-[9999] max-w-[360px] rounded-xl bg-[#19295C] px-4 py-3 text-sm font-medium text-white shadow-xl"
            role="status"
            aria-live="polite"
          >
            Vous êtes hors ligne. Les données seront actualisées dès le retour
            de la connexion.
          </div>
        )}
        <Router />
      </Suspense>
    </>
  );
}

const mapStateToProps = (state) => {
  return {
    isAuthenticated: isAuthenticated(state),
    isLoading: isLoading(state),
    user: state.auth.auth,
  };
};

export default connect(mapStateToProps)(App);
